import { useState, useRef, useEffect } from 'react'
import { apiUrl, authFetch } from '../../lib/api'
import { useAppStore } from '../../store/appStore'

const TEMPLATES = [
  { icon: '🛒', label: 'E-commerce', prompt: 'A modern e-commerce store with product grid, shopping cart, checkout page, and responsive design' },
  { icon: '📊', label: 'Dashboard', prompt: 'An admin dashboard with sidebar navigation, charts, data tables, and dark theme' },
  { icon: '💬', label: 'Chat App', prompt: 'A real-time chat application with message bubbles, user list, and typing indicators' },
  { icon: '📝', label: 'Blog', prompt: 'A blog with article list, single post view, markdown rendering, and categories' },
  { icon: '🎮', label: 'Game', prompt: 'A simple browser game (snake or tic-tac-toe) with score tracking and animations' },
  { icon: '🌤', label: 'Weather', prompt: 'A weather app that shows current weather, 5-day forecast, with city search' },
  { icon: '📋', label: 'Todo App', prompt: 'A todo app with add/edit/delete tasks, categories, due dates, and local storage' },
  { icon: '🖼', label: 'Portfolio', prompt: 'A developer portfolio with hero section, projects grid, skills, and contact form' },
]

export function GenerateAppPanel({ onClose }: { onClose: () => void }) {
  const [prompt, setPrompt] = useState('')
  const [framework, setFramework] = useState('')
  const [multiPage, setMultiPage] = useState(false)

  // Listen for prefill from landing page
  useEffect(() => {
    const handler = (e: Event) => {
      const text = (e as CustomEvent).detail
      if (text) setPrompt(text)
    }
    window.addEventListener('pulse:prefill-prompt', handler)
    return () => window.removeEventListener('pulse:prefill-prompt', handler)
  }, [])
  const [generating, setGenerating] = useState(false)
  const [status, setStatus] = useState('')
  const [files, setFiles] = useState<string[]>([])
  const [done, setDone] = useState(false)
  const [error, setError] = useState('')
  const [streamText, setStreamText] = useState('')
  const [deploying, setDeploying] = useState(false)
  const [deployUrl, setDeployUrl] = useState('')
  const [projectName, setProjectName] = useState('')
  const [collabMode, setCollabMode] = useState(false)
  const logsRef = useRef<HTMLDivElement>(null)
  const roomId = useAppStore(s => s.roomId)

  const generate = async () => {
    if (!prompt.trim() || generating) return
    setGenerating(true)
    setStatus('Starting...')
    setFiles([])
    setDone(false)
    setError('')
    setStreamText('')
    setDeployUrl('')
    setProjectName('')

    try {
      const res = await authFetch(apiUrl('/api/generate'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt, framework: framework || undefined, multiPage, collab: collabMode && roomId ? roomId : undefined }),
      })

      if (!res.body) { setError('No response'); setGenerating(false); return }

      const reader = res.body.getReader()
      const dec = new TextDecoder()
      let buf = ''

      while (true) {
        const { done: streamDone, value } = await reader.read()
        if (streamDone) break
        buf += dec.decode(value, { stream: true })
        const lines = buf.split('\n'); buf = lines.pop() ?? ''
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          try {
            const data = JSON.parse(line.slice(6))
            if (data.type === 'status') setStatus(data.text)
            if (data.type === 'chunk') setStreamText(prev => prev + data.text)
            if (data.type === 'file') setFiles(prev => [...prev, data.path])
            if (data.type === 'error') setError(data.text)
            if (data.type === 'done') {
              setDone(true)
              setProjectName(data.project)
              setStatus(`✓ Created ${data.fileCount} files in /${data.project}`)
              window.dispatchEvent(new CustomEvent('pulse:files-changed'))
            }
          } catch {}
        }
      }
    } catch (e: any) {
      setError(e.message ?? 'Failed')
    } finally {
      setGenerating(false)
    }
  }

  const autoDeploy = async () => {
    setDeploying(true)
    try {
      const res = await authFetch(apiUrl('/api/deploy'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_dir: projectName }),
      })
      if (!res.body) { setDeploying(false); return }
      const reader = res.body.getReader()
      const dec = new TextDecoder()
      let buf = ''
      while (true) {
        const { done: streamDone, value } = await reader.read()
        if (streamDone) break
        buf += dec.decode(value, { stream: true })
        const lines = buf.split('\n'); buf = lines.pop() ?? ''
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          try {
            const data = JSON.parse(line.slice(6))
            if (data.type === 'done' && data.url) {
              const token = JSON.parse(localStorage.getItem('pulse-auth') ?? '{}')?.state?.token ?? ''
              setDeployUrl(`${apiUrl(data.url)}${data.url.includes('?') ? '&' : '?'}token=${token}`)
            }
          } catch {}
        }
      }
    } catch {} finally { setDeploying(false) }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 999, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: 560, maxWidth: '90vw', maxHeight: '85vh', background: 'var(--bg-1)', border: '1px solid var(--border-2)', borderRadius: 16, display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.5)' }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 16, color: 'var(--text-0)' }}>⚡ Generate App</div>
            <div style={{ fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>Describe your app → AI builds it</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px' }}>
          {!generating && !done && (
            <>
              {/* Quick templates */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', letterSpacing: '0.1em', marginBottom: 8 }}>QUICK START</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
                  {TEMPLATES.map(t => (
                    <button key={t.label} onClick={() => setPrompt(t.prompt)} style={{
                      padding: '8px 6px', borderRadius: 8, border: '1px solid var(--border)',
                      background: prompt === t.prompt ? 'rgba(124,110,245,0.1)' : 'var(--bg-2)',
                      color: prompt === t.prompt ? 'var(--accent)' : 'var(--text-1)',
                      cursor: 'pointer', fontSize: 9, fontFamily: 'var(--font-mono)', textAlign: 'center',
                      transition: 'all 0.12s',
                    }}>
                      <div style={{ fontSize: 16, marginBottom: 2 }}>{t.icon}</div>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Prompt input */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', letterSpacing: '0.1em', marginBottom: 6 }}>DESCRIBE YOUR APP</div>
                <textarea
                  value={prompt}
                  onChange={e => setPrompt(e.target.value)}
                  placeholder="A modern todo app with dark theme, categories, due dates, drag-to-reorder, and local storage persistence..."
                  style={{ width: '100%', minHeight: 80, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-2)', background: 'var(--bg-2)', color: 'var(--text-0)', fontFamily: 'var(--font-mono)', fontSize: 12, outline: 'none', resize: 'vertical', lineHeight: 1.5 }}
                />
              </div>

              {/* Multi-page toggle */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', letterSpacing: '0.1em', marginBottom: 6 }}>ROUTING</div>
                <button onClick={() => setMultiPage(!multiPage)} style={{
                  padding: '6px 12px', borderRadius: 8, fontSize: 10, fontFamily: 'var(--font-mono)', fontWeight: 700, cursor: 'pointer',
                  border: multiPage ? '1px solid var(--accent)' : '1px solid var(--border-2)',
                  background: multiPage ? 'rgba(124,110,245,0.1)' : 'none',
                  color: multiPage ? 'var(--accent)' : 'var(--text-2)',
                }}>
                  {multiPage ? '✓ Multi-page (React Router + auth guards + dynamic routes)' : 'Single page — click to enable multi-page routing'}
                </button>
              </div>

              {/* Collab toggle */}
              {roomId && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', letterSpacing: '0.1em', marginBottom: 6 }}>COLLABORATION</div>
                  <button onClick={() => setCollabMode(!collabMode)} style={{
                    padding: '6px 12px', borderRadius: 8, fontSize: 10, fontFamily: 'var(--font-mono)', fontWeight: 700, cursor: 'pointer',
                    border: collabMode ? '1px solid var(--accent-2)' : '1px solid var(--border-2)',
                    background: collabMode ? 'rgba(95,212,164,0.1)' : 'none',
                    color: collabMode ? 'var(--accent-2)' : 'var(--text-2)',
                  }}>
                    {collabMode ? '✓ Shared — teammates see generated files live' : 'Solo — click to share generation with room'}
                  </button>
                </div>
              )}

              {/* Framework selector */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', letterSpacing: '0.1em', marginBottom: 6 }}>FRAMEWORK (optional)</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {['', 'react', 'vue', 'vanilla', 'nextjs', 'express'].map(f => (
                    <button key={f} onClick={() => setFramework(f)} style={{
                      padding: '4px 10px', borderRadius: 20, fontSize: 10, fontFamily: 'var(--font-mono)',
                      border: framework === f ? '1px solid var(--accent)' : '1px solid var(--border-2)',
                      background: framework === f ? 'rgba(124,110,245,0.1)' : 'none',
                      color: framework === f ? 'var(--accent)' : 'var(--text-2)', cursor: 'pointer',
                    }}>{f || 'Auto'}</button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Generating state */}
          {generating && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <span style={{ animation: 'pulse-heartbeat 1.4s infinite', fontSize: 12 }}>⚡</span>
                <span style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--accent)', fontWeight: 700 }}>{status}</span>
              </div>
              {files.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginBottom: 4 }}>Files created:</div>
                  {files.map(f => (
                    <div key={f} style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--accent-2)', padding: '2px 0' }}>📄 {f}</div>
                  ))}
                </div>
              )}
              <div style={{ background: 'var(--bg-0)', border: '1px solid var(--border)', borderRadius: 8, padding: 10, maxHeight: 200, overflow: 'auto', fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--text-1)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                {streamText.slice(-2000)}
                <div ref={logsRef} />
              </div>
            </div>
          )}

          {/* Done state */}
          {done && (
            <div>
              <div style={{ background: 'rgba(95,212,164,0.08)', border: '1px solid rgba(95,212,164,0.3)', borderRadius: 10, padding: 16, marginBottom: 12, textAlign: 'center' }}>
                <div style={{ fontSize: 24, marginBottom: 8 }}>🎉</div>
                <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 13, color: 'var(--accent-2)', marginBottom: 4 }}>{status}</div>
                <div style={{ fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>Check the file tree to see your new project</div>
              </div>
              <div style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginBottom: 8 }}>Generated files:</div>
              {files.map(f => (
                <div key={f} style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--accent-2)', padding: '2px 0' }}>📄 {f}</div>
              ))}
              {/* Auto-deploy button */}
              {!deployUrl && (
                <button onClick={autoDeploy} disabled={deploying} style={{
                  width: '100%', marginTop: 12, padding: '10px', borderRadius: 8,
                  border: 'none', background: deploying ? 'var(--bg-3)' : 'var(--accent)',
                  color: deploying ? 'var(--text-2)' : '#fff', cursor: deploying ? 'wait' : 'pointer',
                  fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 700,
                }}>
                  {deploying ? '🚀 Deploying...' : '🚀 Deploy to Live URL'}
                </button>
              )}
              {deployUrl && (
                <div style={{ marginTop: 12, padding: 12, borderRadius: 8, border: '1px solid var(--accent-2)', background: 'rgba(95,212,164,0.08)' }}>
                  <div style={{ fontSize: 10, color: 'var(--accent-2)', fontFamily: 'var(--font-mono)', fontWeight: 700, marginBottom: 4 }}>🌐 Live URL:</div>
                  <a href={deployUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: 'var(--accent)', fontFamily: 'var(--font-mono)', wordBreak: 'break-all' }}>{deployUrl}</a>
                </div>
              )}
            </div>
          )}

          {error && (
            <div style={{ color: 'var(--red)', fontSize: 11, fontFamily: 'var(--font-mono)', marginTop: 8 }}>⚠️ {error}</div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border)', flexShrink: 0, display: 'flex', gap: 8 }}>
          {!generating && !done && (
            <button onClick={generate} disabled={!prompt.trim()} style={{
              flex: 1, padding: '10px', borderRadius: 8, border: 'none',
              background: prompt.trim() ? 'var(--accent)' : 'var(--bg-3)',
              color: prompt.trim() ? '#fff' : 'var(--text-2)', cursor: prompt.trim() ? 'pointer' : 'not-allowed',
              fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 12, letterSpacing: '0.04em',
            }}>
              ⚡ Generate App
            </button>
          )}
          {done && (
            <>
              <button onClick={() => { setDone(false); setPrompt(''); setFiles([]); setStreamText('') }} style={{
                flex: 1, padding: '10px', borderRadius: 8, border: '1px solid var(--border-2)',
                background: 'none', color: 'var(--text-1)', cursor: 'pointer',
                fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700,
              }}>Generate Another</button>
              <button onClick={onClose} style={{
                flex: 1, padding: '10px', borderRadius: 8, border: 'none',
                background: 'var(--accent-2)', color: '#fff', cursor: 'pointer',
                fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700,
              }}>Open Project</button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
